<template>
  <div>
    <load-profile />

    <balance-report
      :channel-type="channelType"
      :fields="fields"
      :search-fields="searchFields"
      :items="items"
      :total-report="totalReport"
      :params.sync="params"
      :loading="loading"
      :total="total"
      :per-page.sync="perPage"
      :per-page-options="perPageOptions"
      :header-label="showHeaderRow ? user.name : ''"
      :show-back-button="$store.state.profile.id !== user.id"
      :user="user"
      :other-outstanding-fields="['outstanding']"
      @viewMember="viewMember"
      @depositOrWithdrawSuccess="depositOrWithdrawSuccess"
      @search="search"
      @reset="reset"
      @sort-changed="sortChanged"
      @page-change="list"
      @back="back"
      @refresh="getData"
    />
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import Repository from '@/repositories/RepositoryFactory'
import { calculateDatesByPeriod } from '@/libs/helper'
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment'
import BalanceReport from '@/components/report/BalanceReport.vue'
import { GAME_TYPE } from '@/constants/games'
import playerTableFields from './playerTableField'
import searchInputs from './searchInput'
import tableFields from './tableField'

const BalanceReportRepository = Repository.get('balanceReportVvipByType')

export default {
  components: {
    BalanceReport,
  },
  data() {
    return {
      channelType: GAME_TYPE.Vn99,
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      fields: [...tableFields],
      params: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        userId: Number(this.$route.query.userId) || '',
        typeId: Number(this.$route.query.typeId) || '',
        startDate: this.$route.query.startDate || moment().format('YYYY-MM-DD'),
        toDate: this.$route.query.toDate || moment().format('YYYY-MM-DD'),
        period: this.$route.query.period || 'today',
        search: this.$route.query.search || '',
      },
      totalReport: {},
      user: this.$store.state.profile,
      searchFields: [],
      refresh: false,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    showHeaderRow() {
      return this.$store.state.profile.id !== this.user.id
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId
      }
      return this.userData?.typeId
    },
    userData() {
      return this.$store.state.profile.name
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem('userData'))
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    // eslint-disable-next-line func-names
    'params.period': function (value) {
      const dates = calculateDatesByPeriod(value)
      if (dates.length) {
        // eslint-disable-next-line prefer-destructuring
        this.params.startDate = dates[0]
        // eslint-disable-next-line prefer-destructuring
        this.params.toDate = dates[1]
      }
    },
    typeId(value) {
      this.setSearchFields(value)
    },
  },
  mounted() {
    this.getData(this.params.page)
    this.setSearchFields(this.typeId)
  },
  methods: {
    changeCol(value) {
      this.fields = value === 5 ? [...playerTableFields] : [...tableFields]
    },
    setSearchFields(userTypeId) {
      const typeSearch = {
        key: 'typeId',
        label: 'field.type',
        type: 'radio',
        cols: 6,
      }
      if (userTypeId <= 4) {
        typeSearch.options = [
          { value: 5, text: 'breadcrumb.agent' },
          { value: 6, text: 'breadcrumb.player' },
        ]
      } else if (userTypeId === 5) {
        typeSearch.options = [{ value: 6, text: 'breadcrumb.player' }]
      }
      this.searchFields = [...searchInputs, typeSearch]
    },
    back() {
      this.params.userId = null
      this.params.page = 1
      this.updateQuerySting()
      this.getData()
    },
    viewMember(userId) {
      this.params.userId = userId
      this.params.page = 1
      this.updateQuerySting()
      this.getData()
    },
    updateQuerySting() {
      this.$router.replace({ query: this.params })
    },
    list(page = 1) {
      this.params.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy
      this.params.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search(searchText) {
      this.params.page = 1
      this.params.search = searchText
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.params.page = 1
      this.params.userId = null
      this.params.startDate = moment().format('YYYY-MM-DD')
      this.params.toDate = moment().format('YYYY-MM-DD')
      this.params.period = 'today'
      this.params.search = ''
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      BalanceReportRepository.index({
        ...this.params,
        typeId: this.params.typeId ? this.params.typeId : 5,
        channelType: this.channelType,
      })
        .then(response => {
          const { data } = response.data
          if (data.user.typeId <= 4) {
            this.user = { ...this.userData }
            this.changeCol(this.user.typeId)
          } else {
            this.user = { ...data.user }
            if (this.user.typeId) this.changeCol(this.user.typeId)
          }
          setTimeout(() => {
            this.items = [...data.list]
          }, 200)
          this.total = data.total
          this.totalReport = {}
          this.loading = false
          this.getTotal()
        })
        .catch(() => {
          this.loading = false
        })
    },
    getTotal() {
      BalanceReportRepository.total({
        ...this.params,
        typeId: this.params.typeId ? this.params.typeId : 5,
        channelType: this.channelType,
      })
        .then(response => {
          const { data } = response.data
          this.totalReport = { ...data }
          this.refresh = false
        })
        .catch(() => {
          this.refresh = false
        })
    },
    depositOrWithdrawSuccess() {
      this.getData()
    },
  },
}
</script>
